import { Checkbox, Input, Radio } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import {
  CATCH_BOARD_DEAL_SEARCH_DEBOUNCE_MS,
  CATCH_BOARD_OWNER_FILTERS,
  CATCH_BOARD_OWNER_FILTER_META,
  CATCH_BOARD_READINESS_FILTERS,
  CATCH_BOARD_READINESS_FILTER_META,
} from './catchBoardConstants'
import CatchBoardTipLabel from './CatchBoardTipLabel'
import { CATCH_BOARD_TOOLTIPS } from 'src/utils/catchBoardTooltips'

/**
 * Catch Board list filters — ownership, readiness queue, search, hide closed.
 */
const CatchBoardFilters = ({
  ownerFilter,
  readinessFilter,
  search,
  hideClosed,
  disabled = false,
  onOwnerFilterChange,
  onReadinessFilterChange,
  onSearchChange,
  onHideClosedChange,
}) => {
  const [searchDraft, setSearchDraft] = useState(search || '')
  const debounceRef = useRef(null)
  const onSearchChangeRef = useRef(onSearchChange)
  onSearchChangeRef.current = onSearchChange

  useEffect(() => {
    setSearchDraft(search || '')
  }, [search])

  const commitSearch = value => {
    const next = String(value || '').trim()
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    onSearchChangeRef.current?.(next)
  }

  useEffect(() => {
    const next = String(searchDraft || '').trim()
    const current = String(search || '').trim()
    if (next === current) {
      return undefined
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      onSearchChangeRef.current?.(next)
    }, CATCH_BOARD_DEAL_SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [searchDraft, search])

  return (
    <Filters data-testid='catch-board-filters'>
      <OwnerGroup
        value={ownerFilter}
        onChange={event => onOwnerFilterChange?.(event.target.value)}
        disabled={disabled}
        optionType='button'
        buttonStyle='solid'
        size='small'
        data-testid='catch-board-owner-filter'
      >
        {CATCH_BOARD_OWNER_FILTERS.map(key => (
          <Radio.Button key={key} value={key}>
            {CATCH_BOARD_OWNER_FILTER_META[key].label}
          </Radio.Button>
        ))}
      </OwnerGroup>

      <ReadinessWrap>
        <ReadinessGroup
          value={readinessFilter}
          onChange={event => onReadinessFilterChange?.(event.target.value)}
          disabled={disabled}
          optionType='button'
          buttonStyle='solid'
          size='small'
          data-testid='catch-board-readiness-filter'
        >
          {CATCH_BOARD_READINESS_FILTERS.map(key => (
            <Radio.Button key={key} value={key}>
              {CATCH_BOARD_READINESS_FILTER_META[key].label}
            </Radio.Button>
          ))}
        </ReadinessGroup>
        <CatchBoardTipLabel
          iconOnly
          tip={CATCH_BOARD_TOOLTIPS.readinessFilter}
          testId='catch-board-readiness-tip'
        />
      </ReadinessWrap>

      <SearchInput
        allowClear
        disabled={disabled}
        placeholder='Search deals'
        title='Search (/)'
        value={searchDraft}
        onChange={event => setSearchDraft(event.target.value)}
        onSearch={commitSearch}
        onPressEnter={event => commitSearch(event.target.value)}
        data-testid='catch-board-search'
      />

      <HideClosedWrap>
        <Checkbox
          checked={Boolean(hideClosed)}
          disabled={disabled}
          onChange={event => onHideClosedChange?.(event.target.checked)}
          data-testid='catch-board-hide-closed'
        >
          Hide closed
        </Checkbox>
      </HideClosedWrap>
    </Filters>
  )
}

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm || '8px'}
    ${({ theme }) => theme.spacing.md || '16px'};
`

const OwnerGroup = styled(Radio.Group)`
  flex: 0 0 auto;
`

const ReadinessWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`

const ReadinessGroup = styled(Radio.Group)`
  flex: 0 0 auto;
`

const SearchInput = styled(Input.Search)`
  width: min(100%, 260px);
`

const HideClosedWrap = styled.div`
  white-space: nowrap;
`

export default CatchBoardFilters
